import React from 'react';
import { SuggestionWizardProps } from '../../types/suggestionWizard';
import { useWizardConversation } from '../../hooks/suggestionWizard';
import { WizardPanel } from './WizardPanel';
import { WizardHeader } from './WizardHeader';
import { WizardMessages } from './WizardMessages';
import { WizardInputArea } from './WizardInputArea';

/**
 * SuggestionWizard - A conversational interface for generating signal suggestions.
 *
 * Features:
 * - Sliding panel UI with backdrop overlay
 * - Conversational flow to gather ICP information
 * - ICP carousel for selecting existing profiles
 * - Signal generation and selection
 * - Multi-signal selection with batch execution
 *
 * @example
 * ```tsx
 * <SuggestionWizard
 *   isOpen={isWizardOpen}
 *   onClose={() => setIsWizardOpen(false)}
 *   onSelectSuggestion={(query, type, icpId) => handleSignal(query, type, icpId)}
 *   existingICPs={icps}
 *   onICPCreated={(icp) => refreshICPs()}
 * />
 * ```
 */
export const SuggestionWizard: React.FC<SuggestionWizardProps> = ({
  isOpen,
  onClose,
  onSelectSuggestion,
  onSelectMultiple,
  existingICPs,
  preSelectedIcp,
  onICPCreated,
}) => {
  const {
    // State
    isVisible,
    messages,
    currentStep,
    inputValue,
    isTyping,
    isRunningSignal,
    selectedSignals,
    selectedChips,
    expandedDescriptionId,
    expandedSignalName,
    savedSignalNames,

    // Setters
    setInputValue,
    setExpandedDescriptionId,
    setExpandedSignalName,

    // Refs
    messagesEndRef,
    messagesContainerRef,
    inputRef,
    lastUserMessageRef,

    // Handlers
    handleClose,
    handleSubmit,
    handleKeyPress,
    handleOptionSelect,
    handleICPSelect,
    handleToggleSignal,
    handleSaveSignal,
    handleRunSignals,
    handleGenerateNow,
    updateChipSelection,
    clearChips,
  } = useWizardConversation({
    isOpen,
    onClose,
    onSelectSuggestion,
    onSelectMultiple,
    existingICPs,
    preSelectedIcp,
    onICPCreated,
  });

  if (!isOpen) return null;

  return (
    <WizardPanel isVisible={isVisible} onClose={handleClose}>
      <WizardHeader onClose={handleClose} />
      <WizardMessages
        messages={messages}
        isTyping={isTyping}
        selectedChips={selectedChips}
        selectedSignals={selectedSignals}
        savedSignalNames={savedSignalNames}
        expandedDescriptionId={expandedDescriptionId}
        expandedSignalName={expandedSignalName}
        messagesContainerRef={messagesContainerRef}
        messagesEndRef={messagesEndRef}
        lastUserMessageRef={lastUserMessageRef}
        onOptionSelect={handleOptionSelect}
        onChipToggle={updateChipSelection}
        onGenerateNow={handleGenerateNow}
        onICPSelect={handleICPSelect}
        onExpandDescription={setExpandedDescriptionId}
        onToggleSignal={handleToggleSignal}
        onSaveSignal={handleSaveSignal}
        onExpandSignal={setExpandedSignalName}
      />
      <WizardInputArea
        currentStep={currentStep}
        inputValue={inputValue}
        selectedSignalsCount={selectedSignals.size}
        isRunningSignal={isRunningSignal}
        inputRef={inputRef}
        onInputChange={setInputValue}
        onSubmit={handleSubmit}
        onKeyPress={handleKeyPress}
        onRunSignals={handleRunSignals}
        onClearChips={clearChips}
      />
    </WizardPanel>
  );
};

export default SuggestionWizard;
